import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { Features } from "../../../Enums/companyStatus";
import { VoicesCheckSeverity, VoicesPostState } from "../../../Enums/voices";
import { getCurrentUserId, hasPermission } from "../../../Helpers/authHelpers";
import {
  VoicesPost,
  VoicesPostBlocker,
  VoicesPostCheck,
  VoicesPostContent,
  VoicesPostTarget,
} from "../../../Models/voicesPosts";
import {
  useApproveVoicesPost,
  useCreateVoicesPost,
  useDeleteVoicesPost,
  useDraftVoicesPost,
  useEditScheduledVoicesPost,
  useRejectVoicesPost,
  useRetryVoicesPost,
  useStopVoicesPost,
  useSubmitVoicesPost,
  useUpdateVoicesPost,
} from "../../../Query/useVoicesPosts";
import { ComoButton } from "../../Common/ComoButton";
import VoicesConfirmDialog from "../Common/VoicesConfirmDialog";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesTermsInput from "../Common/VoicesTermsInput";
import { nextVoicesPostDate, previewVoicesPostChecks } from "../../../Api/voicesPosts";
import VoicesPostDetails from "./VoicesPostDetails";
import VoicesPostImageField from "./VoicesPostImageField";
import VoicesChecksPanel from "./VoicesChecksPanel";
import VoicesLinkedInPreview from "./VoicesLinkedInPreview";
import VoicesPostStateChip from "./VoicesPostStateChip";
import VoicesTargetSelect from "./VoicesTargetSelect";

interface Props {
  open: boolean;
  campaignId: number | null;
  post: VoicesPost | null;
  topic: { id: number; title: string } | null;
  onClose: () => void;
}

const EMPTY_CONTENT: VoicesPostContent = {
  text: "",
  hooks: [],
  hashtags: [],
  first_comment: null,
  first_comment_enabled: true,
  alt_text: null,
  ai_image: false,
  instructions: null,
  scheduled_at: null,
};

const CONTENT_FIELDS: (keyof VoicesPostContent)[] = [
  "text",
  "hooks",
  "hashtags",
  "first_comment",
  "first_comment_enabled",
  "alt_text",
  "ai_image",
  "scheduled_at",
];

const FORM_BLOCKERS: VoicesPostBlocker[] = ["no_text", "no_date", "date_past"];
const CHECKS_DELAY_MS = 700;

const toInputDateTime = (value: string | null) => (value ? value.slice(0, 16) : "");

const contentOf = (post: VoicesPost | null): VoicesPostContent =>
  post
    ? {
        text: post.text,
        hooks: post.hooks,
        hashtags: post.hashtags,
        first_comment: post.first_comment,
        first_comment_enabled: post.first_comment_enabled,
        alt_text: post.alt_text,
        ai_image: post.ai_image,
        instructions: post.instructions,
        scheduled_at: post.scheduled_at,
      }
    : EMPTY_CONTENT;

const withFirstLine = (text: string, firstLine: string) => {
  const [, ...rest] = text.split("\n");
  return [firstLine, ...rest].join("\n");
};

const textButtonSx = { color: color.gray600, textTransform: "none" };

const VoicesPostDialog: React.FC<Props> = ({ open, campaignId, post, topic, onClose }) => {
  const { t } = useTranslation("voicesPosts");
  const createPost = useCreateVoicesPost();
  const updatePost = useUpdateVoicesPost();
  const deletePost = useDeleteVoicesPost();
  const draftPost = useDraftVoicesPost();
  const submitPost = useSubmitVoicesPost();
  const stopPost = useStopVoicesPost();
  const retryPost = useRetryVoicesPost();
  const approvePost = useApproveVoicesPost();
  const rejectPost = useRejectVoicesPost();
  const editScheduledPost = useEditScheduledVoicesPost();

  const [current, setCurrent] = useState<VoicesPost | null>(post);
  const [form, setForm] = useState<VoicesPostContent>(EMPTY_CONTENT);
  const [target, setTarget] = useState<VoicesPostTarget | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [liveChecks, setLiveChecks] = useState<VoicesPostCheck[] | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [stopNote, setStopNote] = useState("");

  const isNew = !current;
  const editable = !current || current.can_edit;
  const choosesTarget = isNew && hasPermission(Features.VOICES_MARKETING);
  const shownTopic = current ? current.topic : topic;

  useEffect(() => {
    if (!open) return;
    setCurrent(post);
    setTarget(null);
    setForm(contentOf(post));
  }, [open, post?.id]);

  useEffect(() => {
    setLiveChecks(null);
  }, [current?.id, current?.checks]);

  useEffect(() => {
    if (
      !open ||
      !current ||
      !(
        current.can_edit ||
        current.actions.includes("edit_approve") ||
        current.actions.includes("edit_scheduled")
      )
    ) {
      return;
    }
    const timer = setTimeout(() => {
      previewVoicesPostChecks(current.id, form)
        .then((review) => setLiveChecks(review.checks))
        .catch(() => undefined);
    }, CHECKS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, current?.id, form]);

  useEffect(() => {
    if (open && post && current && post.id === current.id && !post.can_edit) {
      setCurrent(post);
    }
  }, [post]);

  const targetName = current?.target.name ?? target?.name ?? t("previewYou");
  const pending =
    createPost.isPending ||
    updatePost.isPending ||
    submitPost.isPending ||
    stopPost.isPending ||
    retryPost.isPending ||
    approvePost.isPending ||
    rejectPost.isPending ||
    editScheduledPost.isPending ||
    drafting;
  const actions = current ? current.actions : [];
  const contentEditable =
    editable || actions.includes("edit_approve") || actions.includes("edit_scheduled");

  const formBlockers: VoicesPostBlocker[] = [];
  if (!form.text.trim()) formBlockers.push("no_text");
  if (!form.scheduled_at) formBlockers.push("no_date");
  else if (new Date(form.scheduled_at) <= new Date()) formBlockers.push("date_past");
  const blockers = [
    ...formBlockers,
    ...(current?.blockers ?? []).filter((blocker) => !FORM_BLOCKERS.includes(blocker)),
  ];
  const showBlockers = editable || actions.includes("approve");
  const checks = liveChecks ?? current?.checks ?? [];
  const blocked =
    blockers.length > 0 ||
    checks.some((check) => check.severity === VoicesCheckSeverity.BLOCK);
  const missingTarget = choosesTarget && !target;

  const persist = (): Promise<VoicesPost> =>
    current
      ? updatePost.mutateAsync({ postId: current.id, data: form })
      : createPost.mutateAsync({
          ...form,
          target,
          campaign_id: campaignId,
          topic_id: topic ? topic.id : null,
        });

  const save = () => persist().then(onClose, () => undefined);

  const suggestDate = async () => {
    setSuggesting(true);
    try {
      const forTarget = current
        ? { kind: current.target.kind, id: current.target.id }
        : target;
      const { scheduled_at } = await nextVoicesPostDate(forTarget);
      if (scheduled_at) setForm((latest) => ({ ...latest, scheduled_at }));
    } catch {
      // the snack already shows what went wrong
    } finally {
      setSuggesting(false);
    }
  };

  const saveForImage = async (): Promise<VoicesPost> => {
    const saved = await persist();
    setCurrent(saved);
    return saved;
  };

  const show = (shown: VoicesPost) => {
    setCurrent(shown);
    setForm(contentOf(shown));
  };

  const submit = async () => {
    try {
      const saved = await persist();
      setCurrent(saved);
      show(await submitPost.mutateAsync(saved.id));
    } catch {
      // the snack already shows what went wrong
    }
  };

  const stopNeedsNote = !!current && current.author?.id !== getCurrentUserId();

  const stop = () =>
    current &&
    stopPost.mutate(
      { postId: current.id, note: stopNeedsNote ? stopNote : "" },
      {
        onSuccess: (stopped) => {
          setConfirmStop(false);
          setStopNote("");
          if (stopped.can_edit) {
            show(stopped);
          } else {
            onClose();
          }
        },
      },
    );

  const retry = () => current && retryPost.mutate(current.id, { onSuccess: show });

  const changedContent = (): Partial<VoicesPostContent> => {
    const changes: Partial<VoicesPostContent> = {};
    if (!current) return changes;
    const original = contentOf(current);
    CONTENT_FIELDS.forEach((field) => {
      if (JSON.stringify(form[field]) !== JSON.stringify(original[field])) {
        Object.assign(changes, { [field]: form[field] });
      }
    });
    return changes;
  };

  const approve = () => {
    if (!current) return;
    const changes = actions.includes("edit_approve") ? changedContent() : {};
    approvePost.mutate({ postId: current.id, changes }, { onSuccess: show });
  };

  const saveScheduled = () =>
    current &&
    editScheduledPost.mutate(
      { postId: current.id, changes: changedContent() },
      {
        onSuccess: (changed) => {
          if (changed.can_edit || changed.actions.length) {
            show(changed);
          } else {
            onClose();
          }
        },
      },
    );

  const scheduledChanges = actions.includes("edit_scheduled") ? changedContent() : {};
  const hasScheduledChanges = Object.keys(scheduledChanges).length > 0;
  const changesNeedApproval =
    hasScheduledChanges &&
    Object.keys(scheduledChanges).some((field) => field !== "scheduled_at") &&
    !(current?.target.kind === "person" && current.target.id === getCurrentUserId());

  const reject = () =>
    current &&
    rejectPost.mutate(
      { postId: current.id, note: rejectNote },
      {
        onSuccess: (rejected) => {
          setRejecting(false);
          setRejectNote("");
          if (rejected.can_edit) {
            show(rejected);
          } else {
            onClose();
          }
        },
      },
    );

  const rejection = current?.approvals.find((approval) => !approval.approved);

  const renderStatus = () => {
    if (!current) return null;
    if (current.publish_error) {
      return (
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: color.red50, border: `1px solid ${color.red200}` }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", color: color.red700 }}>
            {t("publishFailed")}
          </Typography>
          <Typography variant="body2" sx={{ color: color.red700 }}>
            {current.publish_error}
          </Typography>
        </Box>
      );
    }
    if (current.state === VoicesPostState.DRAFT && rejection) {
      return (
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: color.orange50, border: `1px solid ${color.orange200}` }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
            {t(rejection.stopped ? "stoppedBy" : "rejectedBy", { name: rejection.user })}
          </Typography>
          <Typography variant="body2">{rejection.note}</Typography>
        </Box>
      );
    }
    if (current.state === VoicesPostState.IN_APPROVAL) {
      const approved = current.approvals.filter((approval) => approval.approved).length;
      return (
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {actions.includes("approve")
            ? t(actions.includes("edit_approve") ? "statusYourApprovalEdit" : "statusYourApproval")
            : current.approvals_needed > 1
              ? t("statusApprovals", { count: approved, needed: current.approvals_needed })
              : t("statusWaitingFor", { name: current.target.name })}
        </Typography>
      );
    }
    if (current.state === VoicesPostState.PUBLISHED) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: color.gray700 }}>
            {t("statusPublished")}
          </Typography>
          {current.linkedin_url && (
            <Button
              size="small"
              onClick={() =>
                current.linkedin_url &&
                window.open(current.linkedin_url, "_blank", "noopener,noreferrer")
              }
              sx={{ textTransform: "none", color: color.orange600 }}
            >
              {t("engage_open")}
            </Button>
          )}
        </Box>
      );
    }
    const key =
      current.state === VoicesPostState.SCHEDULED
          ? current.in_buffer
            ? "statusInBuffer"
            : "statusHandingOver"
          : null;
    if (!key) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {key === "statusHandingOver" && <CircularProgress size={14} sx={{ color: color.orange600 }} />}
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {t(key)}
        </Typography>
      </Box>
    );
  };

  const draftWithAi = async () => {
    setConfirmDraft(false);
    setDrafting(true);
    try {
      const saved = await persist();
      setCurrent(saved);
      const drafted = await draftPost.mutateAsync(saved.id);
      setCurrent(drafted);
      setForm(contentOf(drafted));
    } catch {
      // the snack already shows what went wrong
    } finally {
      setDrafting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          color: color.gray900,
          fontWeight: 600,
          fontSize: "1.1rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {current ? t("postFor", { name: current.target.name }) : t("newPost")}
        {current && <VoicesPostStateChip post={current} />}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" },
            gap: 3,
          }}
        >
          <Stack spacing={2.5}>
            {shownTopic && (
              <VoicesField label={t("topic")}>
                <Chip
                  size="small"
                  label={shownTopic.title}
                  sx={{
                    backgroundColor: color.orange50,
                    border: `1px solid ${color.orange200}`,
                    color: color.gray900,
                    maxWidth: "100%",
                  }}
                />
              </VoicesField>
            )}
            {choosesTarget && (
              <VoicesField label={t("target")} hint={t("targetHint")}>
                <VoicesTargetSelect value={target} onChange={setTarget} />
              </VoicesField>
            )}
            {renderStatus()}
            {actions.includes("edit_scheduled") && (
              <Typography variant="caption" sx={{ color: color.gray600 }}>
                {t("editScheduledHint")}
              </Typography>
            )}
            {(showBlockers || actions.includes("edit_scheduled")) && (
              <VoicesChecksPanel blockers={blockers} checks={checks} />
            )}
            {editable && (
              <VoicesField label={t("instructions")} hint={t("instructionsHint")}>
                <VoicesInput
                  multiline
                  minRows={2}
                  disabled={drafting}
                  value={form.instructions ?? ""}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value || null })}
                />
              </VoicesField>
            )}
            {editable && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={
                    drafting ? (
                      <CircularProgress size={16} sx={{ color: color.orange600 }} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  disabled={pending || missingTarget}
                  onClick={() => (form.text.trim() ? setConfirmDraft(true) : draftWithAi())}
                  sx={{
                    textTransform: "none",
                    color: color.orange600,
                    borderColor: color.orange600,
                    "&:hover": { borderColor: color.orange600, backgroundColor: color.orange50 },
                    "&.Mui-disabled": { color: color.gray500, borderColor: color.gray200 },
                  }}
                >
                  {drafting ? t("drafting") : t("draftWithAi")}
                </Button>
                <Typography variant="caption">{t("draftHint")}</Typography>
              </Box>
            )}
            {current?.person_note && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: color.orange50,
                  border: `1px solid ${color.orange200}`,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                  {t("personNote", { name: current.target.name })}
                </Typography>
                <Typography variant="body2">{current.person_note}</Typography>
              </Box>
            )}
            <VoicesField label={t("text")}>
              <VoicesInput
                multiline
                minRows={10}
                disabled={!contentEditable || drafting}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
            </VoicesField>
            {contentEditable && form.hooks.length > 0 && (
              <VoicesField label={t("hooks")} hint={t("hooksHint")}>
                <Stack spacing={0.75}>
                  {form.hooks.map((hook) => (
                    <Box
                      key={hook}
                      onClick={() => setForm({ ...form, text: withFirstLine(form.text, hook) })}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${color.gray200}`,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: color.orange50, borderColor: color.orange200 },
                      }}
                    >
                      <Typography variant="body2">{hook}</Typography>
                    </Box>
                  ))}
                </Stack>
              </VoicesField>
            )}
            <VoicesField label={t("hashtags")} hint={t("hashtagsHint")}>
              <VoicesTermsInput
                value={form.hashtags}
                onChange={(hashtags) => contentEditable && setForm({ ...form, hashtags })}
                placeholder={t("hashtagsPlaceholder")}
              />
            </VoicesField>
            <VoicesField label={t("firstComment")} hint={t("firstCommentHint")}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={form.first_comment_enabled}
                    disabled={!contentEditable}
                    onChange={(e) => setForm({ ...form, first_comment_enabled: e.target.checked })}
                    sx={{
                      "& .Mui-checked": { color: `${color.orange600} !important` },
                      "& .Mui-checked + .MuiSwitch-track": {
                        backgroundColor: `${color.orange600} !important`,
                      },
                    }}
                  />
                }
                label={t("firstCommentEnabled")}
                sx={{ mb: 0.5, "& .MuiFormControlLabel-label": { color: color.gray900, fontSize: "0.875rem" } }}
              />
              <VoicesInput
                multiline
                minRows={2}
                disabled={!contentEditable || !form.first_comment_enabled}
                value={form.first_comment ?? ""}
                onChange={(e) => setForm({ ...form, first_comment: e.target.value || null })}
              />
            </VoicesField>
            <VoicesPostImageField
              post={current}
              editable={editable && !missingTarget}
              altText={form.alt_text}
              aiImage={form.ai_image}
              ensureSaved={saveForImage}
              onPostChange={(changed) => setCurrent(changed)}
              onFieldsChange={(fields) => setForm((latest) => ({ ...latest, ...fields }))}
              onImageUrl={setImageUrl}
            />
            <VoicesField label={t("scheduledAt")} hint={t("scheduledAtHint")}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <VoicesInput
                  type="datetime-local"
                  disabled={!contentEditable}
                  value={toInputDateTime(form.scheduled_at)}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value || null })}
                />
                {contentEditable && (
                  <Button
                    onClick={suggestDate}
                    disabled={suggesting || missingTarget}
                    sx={{ ...textButtonSx, whiteSpace: "nowrap" }}
                  >
                    {t("suggestDate")}
                  </Button>
                )}
              </Box>
            </VoicesField>
          </Stack>
          <Box sx={{ position: { md: "sticky" }, top: 0, alignSelf: "start" }}>
            <VoicesField label={t("preview")}>
              <VoicesLinkedInPreview
                name={targetName}
                text={form.text}
                hashtags={form.hashtags}
                firstComment={form.first_comment_enabled ? form.first_comment : null}
                imageUrl={imageUrl}
              />
            </VoicesField>
            {current && (
              <Box sx={{ mt: 2.5 }}>
                <VoicesPostDetails post={current} />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {current?.can_edit && (
          <Button onClick={() => setConfirmDelete(true)} sx={{ ...textButtonSx, mr: "auto" }}>
            {t("delete")}
          </Button>
        )}
        <Button onClick={onClose} sx={textButtonSx}>
          {editable ? t("cancel") : t("closeDialog")}
        </Button>
        {actions.includes("stop") && (
          <Button onClick={() => setConfirmStop(true)} disabled={pending} sx={textButtonSx}>
            {t("stop")}
          </Button>
        )}
        {actions.includes("reject") && (
          <Button onClick={() => setRejecting(true)} disabled={pending} sx={textButtonSx}>
            {t("reject")}
          </Button>
        )}
        {actions.includes("edit_scheduled") && (
          <ComoButton onClick={saveScheduled} disabled={pending || blocked || !hasScheduledChanges}>
            {changesNeedApproval ? t("saveForApproval") : t("saveChanges")}
          </ComoButton>
        )}
        {actions.includes("approve") && (
          <ComoButton onClick={approve} disabled={pending || blocked}>
            {t("approve")}
          </ComoButton>
        )}
        {actions.includes("retry") && (
          <ComoButton onClick={retry} disabled={pending}>
            {t("retry")}
          </ComoButton>
        )}
        {editable && (
          <Button onClick={save} disabled={pending || missingTarget} sx={textButtonSx}>
            {t("save")}
          </Button>
        )}
        {editable && (
          <ComoButton
            onClick={submit}
            disabled={pending || missingTarget || blocked}
          >
            {t("submit")}
          </ComoButton>
        )}
      </DialogActions>

      <VoicesConfirmDialog
        open={confirmDelete}
        title={t("deleteTitle")}
        text={t("deleteConfirm")}
        confirmLabel={t("delete")}
        pending={deletePost.isPending}
        onConfirm={() =>
          current &&
          deletePost.mutate(current.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
            },
          })
        }
        onClose={() => setConfirmDelete(false)}
      />
      <Dialog open={rejecting} onClose={() => setRejecting(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: color.gray900, fontWeight: 600, fontSize: "1.05rem" }}>
          {t("rejectTitle")}
        </DialogTitle>
        <DialogContent>
          <VoicesField label={t("rejectNote")} hint={t("rejectNoteHint")}>
            <VoicesInput
              multiline
              minRows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
          </VoicesField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejecting(false)} sx={textButtonSx}>
            {t("cancel")}
          </Button>
          <ComoButton onClick={reject} disabled={!rejectNote.trim() || rejectPost.isPending}>
            {t("reject")}
          </ComoButton>
        </DialogActions>
      </Dialog>
      <VoicesConfirmDialog
        open={confirmStop && !stopNeedsNote}
        title={t("stopTitle")}
        text={current?.in_buffer ? t("stopConfirmBuffer") : t("stopConfirm")}
        confirmLabel={t("stop")}
        pending={stopPost.isPending}
        onConfirm={stop}
        onClose={() => setConfirmStop(false)}
      />
      <Dialog
        open={confirmStop && stopNeedsNote}
        onClose={() => setConfirmStop(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: color.gray900, fontWeight: 600, fontSize: "1.05rem" }}>
          {t("stopTitle")}
        </DialogTitle>
        <DialogContent>
          <VoicesField
            label={t("stopNote")}
            hint={current?.in_buffer ? t("stopNoteHintBuffer") : t("stopNoteHint")}
          >
            <VoicesInput
              multiline
              minRows={3}
              value={stopNote}
              onChange={(e) => setStopNote(e.target.value)}
            />
          </VoicesField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmStop(false)} sx={textButtonSx}>
            {t("cancel")}
          </Button>
          <ComoButton onClick={stop} disabled={!stopNote.trim() || stopPost.isPending}>
            {t("stop")}
          </ComoButton>
        </DialogActions>
      </Dialog>
      <VoicesConfirmDialog
        open={confirmDraft}
        title={t("draftReplaceTitle")}
        text={t("draftReplaceConfirm")}
        confirmLabel={t("draftWithAi")}
        pending={false}
        onConfirm={draftWithAi}
        onClose={() => setConfirmDraft(false)}
      />
    </Dialog>
  );
};

export default VoicesPostDialog;
